import { FrameViewModel } from 'Lessons/FrameViewModel'; // FIXME: import this differently without getting a circ ref
import { type MessagingService } from 'Lessons/MessagingService.types';
import { angularInjectorProvider } from 'Injector';
import { type OfflineModeManager } from 'OfflineMode';
import { type useDispatch } from 'react-redux';
import {
    type ChatMessage,
    type TutorBotSystemMessage,
    createTutorBotSystemMessage,
    chatActions,
    getLastInitialMessage,
    type RootStateWithChatSlice,
} from 'TutorBotChat';
import { type StructuredMessage } from 'TutorBotChat/types/ChatMessage.types';
import { type FreeResponseClientContext } from 'TutorBotAiTutorChat';
import { getConversationIdForFrameId } from 'TutorBotAiTutorChat/redux/selectors';
import { storeProvider } from 'ReduxHelpers';
import { isStructuredMessage } from 'TutorBotChat/utils/ChatMessage';
import { getConversationMessagesSelector } from 'TutorBotChat/redux/chat/selectors';

import { generateGuid } from 'guid';
import { type FreeResponseFrame } from './FreeResponseFrame';

export class FreeResponseFrameViewModel extends FrameViewModel<FreeResponseFrame> {
    clientContext;
    initialMessage: TutorBotSystemMessage | null = null;
    private completionState = false;
    readonly showOfflineModeMessage: boolean;
    #showSkipButton: boolean = false;
    unsentMessageText: string = '';
    private currentConversationId: string | null = null;

    get showSkipButton(): boolean {
        return this.#showSkipButton;
    }

    get complete(): boolean {
        return this.completionState;
    }

    // The rule that the continue button should be visible when the frame is completed
    // is implemented in FreeResponseButton, so defining it again here is not totally
    // DRY, but having FreeResponseButton read from `complete` directly allows us to use
    // useFrameCompletion
    get continueButtonVisible(): boolean {
        return this.complete || this.showSkipButton;
    }

    // There is already a chat bot on the screen, so it doesn't make sense to click "Explain this screen" to
    // talk to a different one
    get frameSupportsBot(): boolean {
        return false;
    }

    constructor(frame: FreeResponseFrame, _options?: Record<string, unknown>) {
        super(frame, _options);

        const offlineModeManager = angularInjectorProvider.get<OfflineModeManager>('offlineModeManager');
        this.showOfflineModeMessage = offlineModeManager.inOfflineMode;
        if (this.showOfflineModeMessage) {
            this.setCompletionState(true);
        }

        this.clientContext = this.getClientContext();
    }

    // In preview mode, multiple instance of the FreeResponsePlayerUi component live on the
    // screen at the same time, and each one calls this method
    ensureConversationStarted(dispatch: ReturnType<typeof useDispatch>) {
        if (this.showOfflineModeMessage) return;
        const existingConversationId = this.getExistingConversationId();
        const existingInitialMessage = existingConversationId
            ? this.getExistingInitialMessage(existingConversationId)
            : null;
        const conversationId = this.setActiveConversation(dispatch, existingConversationId);
        this.currentConversationId = conversationId;

        if (existingConversationId) {
            this.hydrateStateFromConversation(existingConversationId);
        }

        if (this.initialMessage) return;

        if (existingInitialMessage) {
            this.initialMessage = existingInitialMessage;
            return;
        }

        this.initialMessage = this.getInitialMessage(conversationId);
    }

    private getInitialMessage(conversationId: string) {
        return createTutorBotSystemMessage({
            content: 'START_CONVERSATION' as const, // FIXME START_CONVERSATION_TUTOR_BOT_SYSTEM_MESSAGE_CONTENT,
            payload: {},
            conversationId,
            isInitialMessage: true,
        });
    }

    private setActiveConversation(dispatch: ReturnType<typeof useDispatch>, conversationId: string | null): string {
        const resolvedConversationId = conversationId ?? generateGuid();
        dispatch(
            chatActions.setActiveConversation({
                conversationId: resolvedConversationId,
                conversationMetadata: this.getConversationMetadata(),
            }),
        );

        return resolvedConversationId;
    }

    onMessageUpdate() {
        // We should always have a currentConversationId at this point, but ts requires that
        // we check
        if (!this.currentConversationId) return;
        this.maybeSetCompleteOrSkip(this.currentConversationId);
    }

    private setShowSkipButton(shouldShow: boolean) {
        if (this.#showSkipButton === shouldShow) return;
        this.#showSkipButton = shouldShow;
        this.emit('updated');
    }

    completeAndGotoNextFrame() {
        this.setCompletionState(true);
        this.gotoNextFrame();
    }

    showConfirmationMessage(messageHtml: string) {
        const MessagingService = angularInjectorProvider.get<MessagingService>('Lesson.MessagingService');
        MessagingService.showMessage({
            content: messageHtml,
            correctExists: false, // neutral styling (blue background)
        });
    }

    clearConfirmationMessage() {
        const MessagingService = angularInjectorProvider.get<MessagingService>('Lesson.MessagingService');
        MessagingService.clearMessage();
    }

    onStructuredMessage(_payload: { type: string; [key: string]: unknown }) {
        if (this.showOfflineModeMessage) return;
        // We should always have a currentConversationId at this point, but ts requires that
        // we check
        if (!this.currentConversationId) return;
        this.maybeSetCompleteOrSkip(this.currentConversationId);
    }

    private setCompletionState(isComplete: boolean) {
        if (this.completionState === isComplete) return;
        this.completionState = isComplete;
        if (isComplete) {
            this.emit('completed');
        }
    }

    private hasErroredAgentMessage(messages: ChatMessage[]): boolean {
        return messages.some(message => message.role === 'ai' && !!message.hasError);
    }

    private hasAgentReplyAfterStudentMessage({ messages }: { messages: ChatMessage[] }): boolean {
        const firstHumanMessageIndex = messages.findIndex(message => message.role === 'human');
        if (firstHumanMessageIndex === -1) return false;

        const completedAgentReplies = messages
            .slice(firstHumanMessageIndex)
            .filter(message => message.role === 'ai' && !message.hidden && (message.complete || !!message.hasError));
        return completedAgentReplies.length > 0;
    }

    private getConversationMetadata() {
        return {
            uiContext: this.clientContext.uiContext,
            frameId: this.frame.id,
        };
    }

    private getExistingConversationId(): string | null {
        const store = storeProvider.store;
        if (!store) return null;

        return getConversationIdForFrameId(this.frame.id)(store.getState());
    }

    private getExistingInitialMessage(conversationId: string): TutorBotSystemMessage | null {
        const store = storeProvider.store;
        if (!store) return null;

        const existingInitialMessage = getLastInitialMessage(store.getState(), conversationId);
        if (existingInitialMessage?.role === 'tutor_bot_system') {
            return existingInitialMessage;
        }

        return null;
    }

    private hydrateStateFromConversation(conversationId: string) {
        this.maybeSetCompleteOrSkip(conversationId);
    }

    private getMessagesFromStore(conversationId: string): ChatMessage[] {
        const store = storeProvider.store;
        if (!store) return [];

        const state = store.getState() as RootStateWithChatSlice;

        return getConversationMessagesSelector(conversationId)(state);
    }

    private maybeSetCompleteOrSkip(conversationId: string) {
        if (this.showOfflineModeMessage) return;
        const messages = this.getMessagesFromStore(conversationId);
        if (!messages.length) return;

        const complete = this.maybeSetCompletionState(messages);
        if (complete) return;

        this.maybeSetShowSkipButton(messages);
    }

    private maybeSetCompletionState(messages: ChatMessage[]): boolean {
        const hasTaskCompleteStructuredMessage = messages.some(
            (message): message is StructuredMessage =>
                isStructuredMessage(message) && message.payload?.type === 'taskComplete',
        );
        if (hasTaskCompleteStructuredMessage) {
            this.setCompletionState(true);
            return true;
        }
        return false;
    }

    private maybeSetShowSkipButton(messages: ChatMessage[]) {
        const nonStructuredMessages = messages.filter(message => !isStructuredMessage(message));
        const shouldShowSkip =
            this.hasErroredAgentMessage(nonStructuredMessages) ||
            this.hasAgentReplyAfterStudentMessage({ messages: nonStructuredMessages });

        if (shouldShowSkip) {
            this.setShowSkipButton(true);
        }
    }

    private getClientContext(): FreeResponseClientContext {
        const previousFrameIds = this.previousFrameIds;
        const lesson = this.lesson;
        if (!lesson) {
            throw new Error('Lesson not found');
        }

        return {
            uiContext: 'lesson_player_free_response' as const,
            lessonId: lesson.id,
            lessonVersionId: lesson.version_id,
            activeFrameId: this.frame.id,
            previousFrameIds,
            promptInstructions: this.frame.promptInstructions,
        };
    }
}
